import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '../auth';
import { useToast } from '../use-toast';
import { useIsInFocusedExecution } from '@/contexts/FocusedExecutionContext';
import { fetchFilterData } from '@/lib/filterEngine';
import { resolveCadenceFilterFromFilterData } from '@/lib/cadenceFilterResolver';
import { matchLeadByContactKey } from '@/lib/leads/duplicateLeadKey';
import { ilikeOrValue, phoneIlikeOrValue } from '@/lib/postgrestOr';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  status: 'new' | 'contacted' | 'qualified' | 'meeting' | 'proposal' | 'won' | 'lost';
  source?: string | null;
  last_contact_at?: string | null;
  company_id?: string | null;
  companies?: { name: string | null } | null;
  owner_id?: string | null;
  owner?: { 
    user_id: string;
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
  custom_data?: Record<string, any>;
  created_at: string;
  updated_at: string;
  org_id: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

interface UseLeadsParams {
  search?: string;
  statuses?: string[];
  sources?: string[];
  companies?: string[];
  scores?: string[]; // fit_tier multi-select; '__none__' = sem dado (fit_tier IS NULL)
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterId?: string;
  page?: number;
  pageSize?: number;
  ownerFilter?: string; // When set, only show leads owned by this user_id
}

/**
 * Busca todas as linhas em lotes quando o filtro salvo exige pós-processamento no cliente.
 * PostgREST costuma limitar a ~100 linhas por request; `.limit(N)` não contorna isso sozinho.
 */
async function fetchAllLeadRowsBatched(
  fetchRange: (from: number, to: number) => PromiseLike<{ data: any[] | null; error: any }>
): Promise<any[]> {
  const BATCH_SIZE = 100;
  const MAX_BATCHES = 2000;
  const seen = new Set<string>();
  const out: any[] = [];
  let offset = 0;

  for (let b = 0; b < MAX_BATCHES; b++) {
    const { data: batch, error } = await fetchRange(offset, offset + BATCH_SIZE - 1);
    if (error) throw error;
    const chunk = batch || [];
    if (chunk.length === 0) break;

    let added = 0;
    for (const row of chunk) {
      const id = row?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(row);
      added++;
    }

    if (chunk.length < BATCH_SIZE) break;
    if (added === 0) {
      console.warn(
        "[useLeads] Lote sem leads novos (possível range ignorado pelo backend). Interrompendo paginação em lote."
      );
      break;
    }
    offset += BATCH_SIZE;
  }

  return out;
}

/** Colunas embutidas para regras de filtro hasMany em negócios/atividades vinculados ao lead. */
function appendRelationEmbedsToLeadsSelect(
  baseSelectInner: string,
  embeds: { deals: boolean; activities: boolean }
): string {
  if (!embeds.deals && !embeds.activities) return baseSelectInner;
  const parts: string[] = [];
  if (embeds.deals) {
    parts.push(`deals!deals_lead_id_fkey(
            id,
            title,
            amount,
            probability,
            close_date,
            won_at,
            lost_at,
            pipeline_id,
            stage_id,
            stage,
            loss_reason_id,
            owner_id,
            created_at,
            updated_at,
            lead_source,
            custom_data,
            description,
            company_id,
            org_id,
            lead_id
          )`);
  }
  if (embeds.activities) {
    parts.push(`activities(
            id,
            title,
            type,
            status,
            outcome,
            date,
            owner_id,
            created_at,
            updated_at,
            custom_data,
            deal_id,
            lead_id,
            company_id,
            description,
            notes,
            org_id,
            activity_start_time,
            activity_end_time
          )`);
  }
  const trimmed = baseSelectInner.trimEnd();
  return `${trimmed},\n          ${parts.join(',\n          ')}\n        `;
}

/** Base do `.select()` da lista infinita (sem negócios/atividades embutidos). */
// Sort keys aceitos pelo PostgREST: colunas diretas + embeds presentes no select.
// O array tem que casar com LEADS_SORT_FIELDS em Leads.tsx.
const LEADS_VALID_SORT_KEYS = new Set<string>([
  'name', 'email', 'phone', 'status', 'source', 'last_contact_at', 'created_at',
  'companies(name)',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
]);

// Sort keys aceitos pelo RPC `get_leads_page` — só colunas diretas.
const LEADS_RPC_SORT_KEYS = new Set<string>([
  'name', 'email', 'phone', 'status', 'source', 'last_contact_at', 'created_at',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
]);

const LEADS_INFINITE_BASE_SELECT = `
          id,
          name,
          email,
          phone,
          status,
          source,
          last_contact_at,
          company_id,
          owner_id,
          custom_data,
          created_at,
          updated_at,
          org_id,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_term,
          utm_content,
          companies(id, name, domain, website, industry, size, status, email, phone, instagram, location, address, description, value, faturamento, total_leads, active_leads, converted_leads, owner_id, pipedrive_id, created_at, updated_at, last_contact_at, custom_data)
        `;

export function useLeads(params?: UseLeadsParams) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ['leads', orgId, params],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization not found");

      console.log("🔍 [useLeads] Buscando leads para orgId:", orgId);
      console.log("🔍 [useLeads] Parâmetros:", params);

      // Determinar se há filtro personalizado (que precisa ser aplicado em memória)
      const hasCustomFilter = !!params?.filterId;

      const page = params?.page || 1;
      const pageSize = params?.pageSize || 50;

      const LEADS_QUERY_BASE = `
          id,
          name,
          email,
          phone,
          status,
          source,
          last_contact_at,
          company_id,
          owner_id,
          custom_data,
          created_at,
          updated_at,
          org_id,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_term,
          utm_content,
          companies(id, name, domain, website, industry, size, status, email, phone, instagram, location, address, description, value, faturamento, total_leads, active_leads, converted_leads, owner_id, pipedrive_id, created_at, updated_at, last_contact_at, custom_data)
        `;

      const sortColumn = params?.sortBy || 'created_at';
      const sortAscending = params?.sortOrder === 'asc';
      const orderColumn = LEADS_VALID_SORT_KEYS.has(sortColumn) ? sortColumn : 'created_at';
      // RPC `get_leads_page` só sabe ordenar por colunas diretas; embeds como
      // `companies(name)` viram fallback `created_at` ao chamar a RPC abaixo.
      const rpcOrderColumn = LEADS_RPC_SORT_KEYS.has(orderColumn) ? orderColumn : 'created_at';

      let leadsData: any[] | null;

      if (hasCustomFilter && params?.filterId) {
        // Saved filter: server-side via RPC (build_saved_filter_where translates rules to SQL)
        const filterData = await fetchFilterData(params.filterId, 'leads');
        const cadence = await resolveCadenceFilterFromFilterData(orgId, 'lead', filterData);
        const { data: rpcRows, error } = await supabase.rpc('get_leads_page', {
          p_org_id: orgId,
          p_offset: (page - 1) * pageSize,
          p_limit: pageSize,
          p_search: params?.search?.trim() || null,
          p_statuses: params?.statuses?.length ? params.statuses : null,
          p_sources: params?.sources?.length ? params.sources : null,
          p_company_ids: params?.companies?.length ? params.companies : null,
          p_owner_ids: params?.ownerFilter ? [params.ownerFilter] : null,
          p_include_no_owner: false,
          p_sort_by: rpcOrderColumn,
          p_sort_order: sortAscending ? 'asc' : 'desc',
          p_filter_id: params.filterId,
          p_match_ids: cadence.matchIds,
          p_exclude_ids: cadence.excludeIds,
        });
        if (error) throw error;
        leadsData = (Array.isArray(rpcRows) ? rpcRows : []) as any[];
      } else {
        const buildFilteredQuery = () => {
          let q = supabase.from('leads').select(LEADS_QUERY_BASE).eq('org_id', orgId);

          if (params) {
            if (params.statuses && params.statuses.length > 0) {
              q = q.in('status', params.statuses as never[]);
            }
            if (params.sources && params.sources.length > 0) {
              q = q.in('source', params.sources);
            }
            if (params.companies && params.companies.length > 0) {
              q = q.in('company_id', params.companies);
            }
            if (params.search && params.search.length > 0) {
              q = q.or(
                `name.ilike.${ilikeOrValue(params.search)},email.ilike.${ilikeOrValue(params.search)},phone.ilike.${phoneIlikeOrValue(params.search)}`,
              );
            }
            if (params.ownerFilter) {
              q = q.eq('owner_id', params.ownerFilter);
            }
          }

          return q.order(orderColumn, { ascending: sortAscending });
        };

        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        const { data, error } = await buildFilteredQuery().range(from, to);
        if (error) throw error;
        leadsData = data;
      }

      // Fetch owner profiles for leads that have owner_id
      const ownerIds = [...new Set((leadsData || []).map((lead: any) => lead.owner_id).filter(Boolean))];
      const ownerProfilesMap: Record<string, any> = {};
      
      if (ownerIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, full_name, email, avatar_url')
          .in('user_id', ownerIds);
        
        if (profilesError) {
          console.warn("Aviso ao buscar perfis dos owners:", profilesError);
        } else if (profiles) {
          profiles.forEach(profile => {
            ownerProfilesMap[profile.user_id] = profile;
          });
        }
      }
      
      // Combine leads with owner data
      const data = (leadsData || []).map((lead: any) => ({
        ...lead,
        owner: lead.owner_id ? (ownerProfilesMap[lead.owner_id] || null) : null
      }));
      
      console.log("✅ [useLeads] Leads encontrados:", data.length);

      return data as Lead[];
    },
    enabled: !!orgId
  });
}

// Helper function to apply filter rules
function applyFilterRules(lead: any, filter: any): boolean {
  console.log("🔍 [applyFilterRules] Aplicando regras de filtro para lead:", lead.name);
  console.log("🔍 [applyFilterRules] Dados do filtro:", JSON.stringify(filter, null, 2));
  console.log("🔍 [applyFilterRules] Nome do filtro:", filter.nome_filtro);
  
  const groups = filter.filtro_grupos_condicoes || [];
  console.log("🔍 [applyFilterRules] Grupos encontrados:", groups.length);
  console.log("🔍 [applyFilterRules] Grupos:", groups);
  
  let andResult = true;
  let orResult = false;
  let hasAndRules = false;
  let hasOrRules = false;
  
  for (const group of groups) {
    const rules = group.filtro_regras || [];
    console.log(`🔍 [applyFilterRules] Grupo ${group.tipo_agrupamento} com ${rules.length} regras:`, rules);
    
    if (group.tipo_agrupamento === "AND" && rules.length > 0) {
      hasAndRules = true;
      andResult = rules.every((rule: any) => {
        // Verificar se a regra tem valores válidos
        if (!rule.campo_filtrado || !rule.operador) {
          console.warn(`🔍 [applyFilterRules] Regra AND inválida:`, rule);
          return false;
        }
        const result = evaluateRule(lead, rule);
        console.log(`🔍 [applyFilterRules] Regra AND: ${rule.campo_filtrado} ${rule.operador} ${rule.valor} = ${result}`);
        return result;
      });
    } else if (group.tipo_agrupamento === "OR" && rules.length > 0) {
      hasOrRules = true;
      orResult = rules.some((rule: any) => {
        // Verificar se a regra tem valores válidos
        if (!rule.campo_filtrado || !rule.operador) {
          console.warn(`🔍 [applyFilterRules] Regra OR inválida:`, rule);
          return false;
        }
        const result = evaluateRule(lead, rule);
        console.log(`🔍 [applyFilterRules] Regra OR: ${rule.campo_filtrado} ${rule.operador} ${rule.valor} = ${result}`);
        return result;
      });
    }
  }
  
  console.log("🔍 [applyFilterRules] Resultados:", { hasAndRules, andResult, hasOrRules, orResult });
  
  // If both AND and OR rules exist, both must be satisfied
  // If only AND rules exist, they must be satisfied
  // If only OR rules exist, at least one must be satisfied
  if (hasAndRules && hasOrRules) {
    const finalResult = andResult && orResult;
    console.log("🔍 [applyFilterRules] Resultado final (AND + OR):", finalResult);
    return finalResult;
  } else if (hasAndRules) {
    console.log("🔍 [applyFilterRules] Resultado final (AND):", andResult);
    return andResult;
  } else if (hasOrRules) {
    console.log("🔍 [applyFilterRules] Resultado final (OR):", orResult);
    return orResult;
  }
  
  console.log("🔍 [applyFilterRules] Nenhuma regra encontrada, retornando true");
  return true;
}

// Helper function to evaluate individual rules
function evaluateRule(lead: any, rule: any): boolean {
  const fieldValue = getFieldValue(lead, rule.campo_filtrado);
  const ruleValue = rule.valor;
  
  console.log(`🔍 [evaluateRule] Campo: ${rule.campo_filtrado}, Valor: ${fieldValue}, Operador: ${rule.operador}, Regra: ${ruleValue}`);
  
  // Handle owner_id field with "__no_owner__" special value
  if (rule.campo_filtrado === "owner_id") {
    const leadOwnerId = lead.owner_id || null;
    if (ruleValue === "__no_owner__") {
      // "Sem proprietário" - retornar itens sem owner_id
      switch (rule.operador) {
        case 'equals':
          return leadOwnerId === null || leadOwnerId === undefined || leadOwnerId === "";
        case 'not_equals':
          return leadOwnerId !== null && leadOwnerId !== undefined && leadOwnerId !== "";
        default:
          return leadOwnerId === null || leadOwnerId === undefined || leadOwnerId === "";
      }
    }
  }
  
  switch (rule.operador) {
    case 'equals':
      // Handle "__no_owner__" for owner_id fields
      if (rule.campo_filtrado === "owner_id" && ruleValue === "__no_owner__") {
        return !fieldValue || fieldValue === null || fieldValue === undefined || fieldValue === "";
      }
      return fieldValue === ruleValue;
    case 'not_equals':
      return fieldValue !== ruleValue;
    case 'contains':
      return String(fieldValue).toLowerCase().includes(String(ruleValue).toLowerCase());
    case 'not_contains':
      return !String(fieldValue).toLowerCase().includes(String(ruleValue).toLowerCase());
    case 'starts_with':
      return String(fieldValue).toLowerCase().startsWith(String(ruleValue).toLowerCase());
    case 'ends_with':
      return String(fieldValue).toLowerCase().endsWith(String(ruleValue).toLowerCase());
    case 'is_empty':
      return !fieldValue || fieldValue === '';
    case 'is_not_empty':
      return fieldValue && fieldValue !== '';
    case 'greater_than':
      return Number(fieldValue) > Number(ruleValue);
    case 'less_than':
      return Number(fieldValue) < Number(ruleValue);
    case 'greater_than_or_equal':
      return Number(fieldValue) >= Number(ruleValue);
    case 'less_than_or_equal':
      return Number(fieldValue) <= Number(ruleValue);
    case 'in':
      return ruleValue.split(',').map((v: string) => v.trim()).includes(String(fieldValue));
    case 'not_in':
      return !ruleValue.split(',').map((v: string) => v.trim()).includes(String(fieldValue));
    default:
      console.warn(`Operador desconhecido: ${rule.operador}`);
      return true;
  }
}

// Helper function to get field value from lead object
function getFieldValue(lead: any, field: string): any {
  // Handle custom fields
  if (field.startsWith('custom_data.')) {
    const customFieldName = field.replace('custom_data.', '');
    return lead.custom_data?.[customFieldName] || '';
  }
  
  switch (field) {
    case 'name':
      return lead.name || '';
    case 'email':
      return lead.email || '';
    case 'phone':
      return lead.phone || '';
    case 'status':
      return lead.status || '';
    case 'source':
      return lead.source || '';
    case 'owner_id':
      return lead.owner_id || '';
    case 'company_name':
      return lead.companies?.name || '';
    case 'last_contact_at':
      return lead.last_contact_at || '';
    case 'created_at':
      return lead.created_at || '';
    default:
      console.warn(`Campo desconhecido: ${field}`);
      return '';
  }
}

const LEADS_PAGE_SIZE = 50;

interface LeadsPage {
  items: Lead[];
  total: number;
  nextOffset: number | undefined;
}

type UseLeadsInfiniteParams = Omit<UseLeadsParams, 'page' | 'pageSize'> & {
  /** Incrementado ao aplicar filtro salvo na UI — força nova busca (mesmo id na URL). */
  filterApplyRevision?: number;
};

export function useLeadsInfinite(params?: UseLeadsInfiniteParams) {
  const { orgId } = useCurrentOrgId();
  const rev = params?.filterApplyRevision ?? 0;

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isFetching,
  } = useInfiniteQuery<LeadsPage, Error, { pages: LeadsPage[] }, unknown[], number>({
    queryKey: [
      'leads-infinite',
      orgId,
      params?.search ?? '',
      (params?.statuses ?? []).join(','),
      (params?.sources ?? []).join(','),
      (params?.companies ?? []).join(','),
      (params?.scores ?? []).join(','),
      params?.sortBy ?? 'created_at',
      params?.sortOrder ?? 'desc',
      params?.filterId ?? null,
      params?.ownerFilter ?? null,
      rev,
    ],
    queryFn: async ({ pageParam }) => {
      if (!orgId) throw new Error("Organization not found");

      const hasCustomFilter = !!params?.filterId;
      /** TanStack Query pode enviar `pageParam` indefinido na 1ª página; `undefined !== 0` é true e esvaziava a lista. */
      const pageOffset = pageParam ?? 0;
      const from = pageOffset;
      const to = from + LEADS_PAGE_SIZE - 1;

      const sortColumn = params?.sortBy || 'created_at';
      const sortAscending = params?.sortOrder === 'asc';
      const orderColumn = LEADS_VALID_SORT_KEYS.has(sortColumn) ? sortColumn : 'created_at';
      const rpcOrderColumn = LEADS_RPC_SORT_KEYS.has(orderColumn) ? orderColumn : 'created_at';

      const fetchOwnerProfiles = async (rows: any[]) => {
        const ownerIds = [...new Set((rows || []).map((lead: any) => lead.owner_id).filter(Boolean))];
        const ownerProfilesMap: Record<string, any> = {};
        if (ownerIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, full_name, email, avatar_url')
            .in('user_id', ownerIds);
          if (profiles) {
            profiles.forEach((profile: any) => { ownerProfilesMap[profile.user_id] = profile; });
          }
        }
        return (rows || []).map((lead: any) => ({
          ...lead,
          owner: lead.owner_id ? (ownerProfilesMap[lead.owner_id] || null) : null,
        })) as Lead[];
      };

      if (hasCustomFilter && params?.filterId) {
        // Filtro salvo: server-side via RPC get_leads_page (build_saved_filter_where traduz regras em SQL)
        const filterData = await fetchFilterData(params.filterId, 'leads');
        const cadence = await resolveCadenceFilterFromFilterData(orgId, 'lead', filterData);
        const [pageRes, countRes] = await Promise.all([
          supabase.rpc('get_leads_page', {
            p_org_id: orgId,
            p_offset: from,
            p_limit: LEADS_PAGE_SIZE,
            p_search: params?.search?.trim() || null,
            p_statuses: params?.statuses?.length ? params.statuses : null,
            p_sources: params?.sources?.length ? params.sources : null,
            p_company_ids: params?.companies?.length ? params.companies : null,
            p_owner_ids: params?.ownerFilter ? [params.ownerFilter] : null,
            p_include_no_owner: false,
            p_sort_by: rpcOrderColumn,
            p_sort_order: sortAscending ? 'asc' : 'desc',
            p_filter_id: params.filterId,
            p_match_ids: cadence.matchIds,
            p_exclude_ids: cadence.excludeIds,
          }),
          // só pega count na primeira página — economiza ida ao banco em scrolls
          pageOffset === 0
            ? supabase.rpc('get_leads_count', {
                p_org_id: orgId,
                p_search: params?.search?.trim() || null,
                p_statuses: params?.statuses?.length ? params.statuses : null,
                p_sources: params?.sources?.length ? params.sources : null,
                p_company_ids: params?.companies?.length ? params.companies : null,
                p_owner_ids: params?.ownerFilter ? [params.ownerFilter] : null,
                p_include_no_owner: false,
                p_filter_id: params.filterId,
                p_match_ids: cadence.matchIds,
                p_exclude_ids: cadence.excludeIds,
              })
            : Promise.resolve({ data: null, error: null }),
        ]);
        if (pageRes.error) throw pageRes.error;
        const rpcRows = (Array.isArray(pageRes.data) ? pageRes.data : []) as any[];
        const items = await fetchOwnerProfiles(rpcRows);
        const total = pageOffset === 0 ? Number(countRes.data ?? 0) : (data?.pages[0]?.total ?? items.length);
        return {
          items,
          total,
          nextOffset: items.length === LEADS_PAGE_SIZE ? from + LEADS_PAGE_SIZE : undefined,
        };
      }

      // Sem filtro salvo: paginação normal server-side com PostgREST
      const buildBaseQuery = () => {
        let q = supabase.from("leads").select(LEADS_INFINITE_BASE_SELECT, { count: "exact" }).eq("org_id", orgId);
        if (params) {
          if (params.statuses && params.statuses.length > 0) {
            q = q.in('status', params.statuses as never[]);
          }
          if (params.sources && params.sources.length > 0) {
            q = q.in('source', params.sources);
          }
          if (params.companies && params.companies.length > 0) {
            q = q.in('company_id', params.companies);
          }
          if (params.scores && params.scores.length > 0) {
            const includeNone = params.scores.includes('__none__');
            const tiers = params.scores.filter((s) => s !== '__none__');
            if (includeNone && tiers.length > 0) q = q.or(`fit_tier.in.(${tiers.join(',')}),fit_tier.is.null`);
            else if (includeNone) q = q.is('fit_tier', null);
            else q = q.in('fit_tier', tiers);
          }
          if (params.search && params.search.length > 0) {
            q = q.or(
              `name.ilike.${ilikeOrValue(params.search)},email.ilike.${ilikeOrValue(params.search)},phone.ilike.${phoneIlikeOrValue(params.search)}`,
            );
          }
          if (params.ownerFilter) {
            q = q.eq('owner_id', params.ownerFilter);
          }
        }
        return q.order(orderColumn, { ascending: sortAscending });
      };

      const { data: leadsData, error: queryError, count } = await buildBaseQuery().range(from, to);
      if (queryError) throw queryError;
      const items = await fetchOwnerProfiles((leadsData || []) as any[]);
      return {
        items,
        total: count || 0,
        nextOffset: items.length === LEADS_PAGE_SIZE ? from + LEADS_PAGE_SIZE : undefined,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });

  const leads = useMemo(() => data?.pages.flatMap(p => p.items) || [], [data]);
  const totalCount = data?.pages[0]?.total || 0;

  return {
    leads,
    totalCount,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isRefetching,
    isFetching,
  };
}

/**
 * Fetches ALL lead IDs matching the current filters (lightweight query, no joins).
 * Returns [] if client-side processing is needed (caller should use already-loaded data).
 */
export async function fetchAllFilteredLeadIds(
  orgId: string,
  params?: {
    statuses?: string[];
    sources?: string[];
    companies?: string[];
    search?: string;
    ownerFilter?: string;
    filterId?: string;
  }
): Promise<string[]> {
  // Filtro salvo: pagina server-side via get_leads_page (mesmas regras da tabela).
  // ANTES retornava [] assumindo que o hook carregava tudo — mas hoje o hook pagina
  // (50/página), então "selecionar todos" só pegava a página. Aqui buscamos TODOS
  // os IDs do filtro em lotes, com as mesmas regras/cadência da view.
  if (params?.filterId) {
    const filterData = await fetchFilterData(params.filterId, 'leads');
    const cadence = await resolveCadenceFilterFromFilterData(orgId, 'lead', filterData);
    // 100 = teto de linhas por resposta do PostgREST (mesmo BATCH do caminho sem
    // filtro salvo). Usar mais que isso faz o loop parar cedo (resposta < batch).
    const RPC_BATCH = 100;
    const ids: string[] = [];
    let rpcOffset = 0;
    while (true) {
      const { data, error } = await supabase.rpc('get_leads_page', {
        p_org_id: orgId,
        p_offset: rpcOffset,
        p_limit: RPC_BATCH,
        p_search: params.search?.trim() || null,
        p_statuses: params.statuses?.length ? params.statuses : null,
        p_sources: params.sources?.length ? params.sources : null,
        p_company_ids: params.companies?.length ? params.companies : null,
        p_owner_ids: params.ownerFilter ? [params.ownerFilter] : null,
        p_include_no_owner: false,
        p_sort_by: 'created_at',
        p_sort_order: 'desc',
        p_filter_id: params.filterId,
        p_match_ids: cadence.matchIds,
        p_exclude_ids: cadence.excludeIds,
      });
      if (error) throw error;
      const rows = (Array.isArray(data) ? data : []) as { id: string }[];
      ids.push(...rows.map((r) => r.id));
      if (rows.length < RPC_BATCH) break;
      rpcOffset += RPC_BATCH;
    }
    return ids;
  }

  // Use batch size matching PostgREST configured limit
  const BATCH = 100;
  const allIds: string[] = [];
  let offset = 0;

  const buildIdsQuery = () => {
    let q = supabase
      .from('leads')
      .select('id')
      .eq('org_id', orgId);

    if (params) {
      if (params.statuses && params.statuses.length > 0) {
        q = q.in('status', params.statuses as never[]);
      }
      if (params.sources && params.sources.length > 0) {
        q = q.in('source', params.sources);
      }
      if (params.companies && params.companies.length > 0) {
        q = q.in('company_id', params.companies);
      }
      if (params.search && params.search.length > 0) {
        q = q.or(
          `name.ilike.${ilikeOrValue(params.search)},email.ilike.${ilikeOrValue(params.search)},phone.ilike.${phoneIlikeOrValue(params.search)}`,
        );
      }
      if (params.ownerFilter) {
        q = q.eq('owner_id', params.ownerFilter);
      }
    }

    return q;
  };

  while (true) {
    const { data, error } = await buildIdsQuery().range(offset, offset + BATCH - 1);
    if (error) throw error;
    const batch = data || [];
    allIds.push(...batch.map((d: { id: string }) => d.id));
    if (batch.length < BATCH) break;
    offset += BATCH;
  }

  return allIds;
}

/**
 * Server-side count of leads matching the given filters.
 * Routes through get_leads_count when filterId is set so saved-filter rules
 * are applied server-side and the total matches the table view.
 */
function useLeadsCount(params?: {
  search?: string;
  statuses?: string[];
  sources?: string[];
  companies?: string[];
  ownerFilter?: string;
  filterId?: string | null;
}) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ['leads-count', orgId, params],
    queryFn: async () => {
      if (!orgId) return 0;

      const filterData = params?.filterId ? await fetchFilterData(params.filterId, 'leads') : null;
      const cadence = await resolveCadenceFilterFromFilterData(orgId, 'lead', filterData);
      const { data, error } = await supabase.rpc('get_leads_count', {
        p_org_id: orgId,
        p_search: params?.search?.trim() || null,
        p_statuses: params?.statuses?.length ? params.statuses : null,
        p_sources: params?.sources?.length ? params.sources : null,
        p_company_ids: params?.companies?.length ? params.companies : null,
        p_owner_ids: params?.ownerFilter ? [params.ownerFilter] : null,
        p_include_no_owner: false,
        p_filter_id: params?.filterId || null,
        p_match_ids: cadence.matchIds,
        p_exclude_ids: cadence.excludeIds,
      });
      if (error) {
        console.error('Erro ao contar leads:', error);
        return 0;
      }
      return Number(data ?? 0);
    },
    enabled: !!orgId,
  });
}

/** Lead já existente devolvido quando a criação colide com `unique_lead_per_org_idx`. */
export type DuplicateLead = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  company_id: string | null;
};

type DuplicateLeadError = Error & {
  code?: string;
  isDuplicateLead?: true;
  existingLead?: DuplicateLead | null;
};

function isDuplicateLeadError(error: unknown): error is DuplicateLeadError {
  return !!error && typeof error === 'object' && (error as DuplicateLeadError).isDuplicateLead === true;
}

/**
 * Localiza o lead que colidiu com `unique_lead_per_org_idx`.
 *
 * Busca os candidatos por `(org_id, name)` e delega o casamento de telefone/e-mail a
 * `matchLeadByContactKey`, que replica a semântica da chave (COALESCE + normalização de
 * telefone do trigger). Uma query só — os fallbacks rodam sobre os mesmos candidatos — e
 * medida em ~20ms numa org de 24k leads; roda apenas no caminho de erro 23505.
 */
async function findExistingLeadByIndexKey(
  orgId: string,
  key: { name: string; phone: string | null; email: string | null },
): Promise<DuplicateLead | null> {
  const { data } = await supabase
    .from('leads')
    .select('id, name, email, phone, company_id')
    .eq('org_id', orgId)
    .eq('name', key.name)
    .limit(200);

  return matchLeadByContactKey((data ?? []) as DuplicateLead[], key);
}

// Hook para criar leads
export function useCreateLead(options?: {
  /**
   * Chamado quando a criação colidiu com um lead que já existe na org, recebendo esse
   * lead (ou `null` se não foi possível identificá-lo). Quando informado, o hook **não**
   * exibe o toast de duplicidade — quem trata é o chamador, que normalmente reaproveita
   * o lead existente em vez de falhar.
   */
  onDuplicate?: (existingLead: DuplicateLead | null) => void;
}) {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (leadData: {
      name: string;
      email?: string | null;
      phone?: string;
      company_id?: string;
      source?: string | null;
      status?: 'new' | 'contacted' | 'qualified' | 'meeting' | 'proposal' | 'won' | 'lost';
      owner_id?: string | null;
      companyName?: string;
      website?: string;
      industry?: string;
      custom_data?: Record<string, any>;
    }) => {
      if (!orgId) throw new Error("Organization not found");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Handle company logic
      let companyId = leadData.company_id;
      
      // If we have company data but no company_id, create or find company
      if (leadData.companyName && !companyId) {
        // Check if company exists
        const { data: existingCompany } = await supabase
          .from('companies')
          .select('id')
          .eq('name', leadData.companyName)
          .eq('org_id', orgId)
          .maybeSingle();

        if (existingCompany) {
          companyId = existingCompany.id;
        } else {
          // Create new company
          const { data: newCompany, error: companyError } = await supabase
            .from('companies')
            .insert({
              org_id: orgId,
              name: leadData.companyName,
              website: leadData.website || null,
              industry: leadData.industry || null,
              created_by: user.id,
            })
            .select()
            .single();

          if (companyError) throw companyError;
          companyId = newCompany.id;
        }
      }

      const { data, error } = await supabase
        .from('leads')
        .insert({
          org_id: orgId,
          created_by: user.id,
          name: leadData.name,
          email: leadData.email?.trim() || null,
          phone: leadData.phone || null,
          company_id: companyId || null,
          source: leadData.source?.trim() || null,
          status: leadData.status || 'new',
          value: 0,
          owner_id: leadData.owner_id || null,
          custom_data: leadData.custom_data || null,
        })
        .select()
        .single();

      if (error) {
        // 23505 = unique_violation: a org já tem um lead que colide com a chave do
        // índice `unique_lead_per_org_idx`. Em vez de repassar a mensagem crua do
        // Postgres, identifica o lead existente e lança um erro tipado — os chamadores
        // que sabem o que fazer com ele (vincular ao negócio, por exemplo) tratam via
        // `options.onDuplicate`.
        if (error.code === '23505') {
          const existingLead = await findExistingLeadByIndexKey(orgId, {
            name: leadData.name,
            phone: leadData.phone || null,
            email: leadData.email?.trim() || null,
          });

          const duplicateError = new Error(
            existingLead?.name
              ? `Já existe um lead com esses dados nesta organização: "${existingLead.name}".`
              : 'Já existe um lead com esses dados nesta organização.',
          ) as DuplicateLeadError;
          duplicateError.code = '23505';
          duplicateError.isDuplicateLead = true;
          duplicateError.existingLead = existingLead;
          throw duplicateError;
        }
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Invalidar todas as queries de leads (lista paginada + infinita)
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      queryClient.invalidateQueries({ queryKey: ['leads', orgId, undefined] });
      
      // Forçar refetch para atualizar a lista imediatamente
      queryClient.refetchQueries({ queryKey: ['leads'] });
      queryClient.refetchQueries({ queryKey: ['leads-infinite'] });
      
      toast({
        title: "Lead criado",
        description: "O lead foi criado com sucesso.",
      });
    },
    onError: (error: any) => {
      // O chamador assumiu o tratamento da duplicidade (ex.: vincular o lead existente
      // ao negócio) — não emite toast de erro para não competir com o dele.
      if (isDuplicateLeadError(error) && options?.onDuplicate) {
        options.onDuplicate(error.existingLead ?? null);
        return;
      }
      if (isDuplicateLeadError(error) || error?.code === '23505') {
        toast({
          title: "Lead já existe",
          description:
            error.message || "Já existe um lead com esses dados nesta organização.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Erro ao criar lead",
        description: error.message || "Ocorreu um erro ao criar o lead.",
        variant: "destructive",
      });
    },
  });
}

// Hook para atualizar leads
export function useUpdateLead() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();
  const isInFocusedExecution = useIsInFocusedExecution();

  return useMutation({
    mutationFn: async ({ 
      id, 
      leadData 
    }: { 
      id: string; 
      leadData: {
        name?: string;
        email?: string;
        phone?: string;
        company_id?: string;
        source?: string;
        status?: 'new' | 'contacted' | 'qualified' | 'meeting' | 'proposal' | 'won' | 'lost';
        owner_id?: string | null;
        currentCompanyId?: string | null;
        companyName?: string;
        website?: string;
        industry?: string;
        notes?: string;
        custom_data?: Record<string, any>;
        loss_reason_id?: string | null;
        custom_loss_reason?: string | null;
      }
    }) => {
      if (!orgId) throw new Error("Organization not found");


      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Prepare lead update data
      const leadUpdateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Add lead fields
      if (leadData.name !== undefined) leadUpdateData.name = leadData.name;
      if (leadData.email !== undefined) leadUpdateData.email = leadData.email;
      if (leadData.phone !== undefined) leadUpdateData.phone = leadData.phone;
      if (leadData.source !== undefined) leadUpdateData.source = leadData.source;
      if (leadData.status !== undefined) leadUpdateData.status = leadData.status;
      if (leadData.owner_id !== undefined) leadUpdateData.owner_id = leadData.owner_id;
      if (leadData.custom_data !== undefined) leadUpdateData.custom_data = leadData.custom_data;
      if (leadData.loss_reason_id !== undefined) leadUpdateData.loss_reason_id = leadData.loss_reason_id;
      if (leadData.custom_loss_reason !== undefined) leadUpdateData.custom_loss_reason = leadData.custom_loss_reason;

      // Handle company logic
      let companyId = leadData.company_id || leadData.currentCompanyId;
      
      // If we have company data but no company_id, create or find company
      if (leadData.companyName && !companyId) {
        // Check if company exists
        const { data: existingCompany } = await supabase
          .from('companies')
          .select('id')
          .eq('name', leadData.companyName)
          .eq('org_id', orgId)
          .maybeSingle();

        if (existingCompany) {
          companyId = existingCompany.id;
        } else {
          // Create new company
          const { data: newCompany, error: companyError } = await supabase
            .from('companies')
            .insert({
              org_id: orgId,
              name: leadData.companyName,
              website: leadData.website || null,
              industry: leadData.industry || null,
            })
            .select()
            .single();

          if (companyError) throw companyError;
          companyId = newCompany.id;
        }
      }

      if (companyId) {
        leadUpdateData.company_id = companyId;
      }

      // Update lead
      let { data: updatedLead, error: leadError } = await supabase
        .from('leads')
        .update(leadUpdateData)
        .eq('id', id)
        .eq('org_id', orgId)
        .select()
        .single();

      if (leadError) {
        // Rede de segurança: a policy RESTRICTIVE `leads_visibility_exception_select`
        // esconde a linha pós-update no RETURNING quando o novo `owner_id` cai numa
        // regra de `record_visibility_exceptions` e o viewer não tem bypass — o
        // `.select()` falha com 42501 "new row violates row-level security policy"
        // embora o UPDATE em si tenha sido aplicado. Nesse caso refazemos sem
        // RETURNING e sintetizamos o retorno. Mesmo padrão de `useDeals.ts`.
        const isRlsReturningError =
          (leadError as { code?: string }).code === '42501' ||
          /row-level security/i.test((leadError as { message?: string }).message ?? '');

        if (!isRlsReturningError) {
          console.error('❌ [useUpdateLead] Error updating lead:', leadError);
          throw leadError;
        }

        const { error: retryError } = await supabase
          .from('leads')
          .update(leadUpdateData)
          .eq('id', id)
          .eq('org_id', orgId);

        if (retryError) {
          console.error('❌ [useUpdateLead] Error updating lead:', retryError);
          throw retryError;
        }

        updatedLead = { id, ...leadUpdateData };
        leadError = null;
      }


      // Update company if we have company data and company exists
      if (companyId && (leadData.website !== undefined || leadData.industry !== undefined)) {
        const companyUpdateData: any = {};
        if (leadData.website !== undefined) companyUpdateData.website = leadData.website;
        if (leadData.industry !== undefined) companyUpdateData.industry = leadData.industry;

        if (Object.keys(companyUpdateData).length > 0) {
          const { error: companyUpdateError } = await supabase
            .from('companies')
            .update(companyUpdateData)
            .eq('id', companyId)
            .eq('org_id', orgId);

          if (companyUpdateError) {
            console.warn('Failed to update company:', companyUpdateError);
            // Don't throw error here, lead was updated successfully
          }
        }
      }

      return updatedLead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      queryClient.invalidateQueries({ queryKey: ['lead'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['companies', orgId] });
      if (!isInFocusedExecution) {
        toast({
          title: "Lead atualizado",
          description: "O lead foi atualizado com sucesso.",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar lead",
        description: error.message || "Ocorreu um erro ao atualizar o lead.",
        variant: "destructive",
      });
    },
  });
}